(function () {
  "use strict";

  const STATUS_AVAILABLE = "available";
  const STATUS_UNAVAILABLE = "unavailable";
  const STATUS_INCLUDED = "included";
  const STATUS_OUTSIDE_SERVICE_AREA = "outside_service_area";

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function toArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function normalizeText(value) {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
  }

  function normalizeSlot(slot) {
    const source = slot || {};

    return {
      date: typeof source.date === "string" ? source.date : "",
      time: typeof source.time === "string" ? source.time : "",
      location: typeof source.location === "string" ? source.location : ""
    };
  }

  function normalizeAvailabilityData(availabilityData) {
    const source = availabilityData || {};

    return {
      bookings: clone(toArray(source.bookings)),
      equipment: clone(toArray(source.equipment)),
      staff: clone(toArray(source.staff)),
      travel: clone(source.travel || {}),
      serviceAreas: clone(toArray(source.serviceAreas))
    };
  }

  function matchesValue(recordValue, slotValue) {
    return !recordValue || !slotValue || normalizeText(recordValue) === normalizeText(slotValue);
  }

  function matchesSlot(record, slot) {
    return matchesValue(record.date, slot.date) &&
      matchesValue(record.time, slot.time) &&
      matchesValue(record.location || record.city || record.serviceArea, slot.location);
  }

  function isInactive(record) {
    return record.available === false ||
      record.active === false ||
      record.enabled === false ||
      normalizeText(record.status) === "unavailable" ||
      normalizeText(record.status) === "inactive";
  }

  function getExperienceId(record) {
    return String(record.experienceId || record.experience || record.id || "");
  }

  function getExperienceTitle(record) {
    return String(record.experienceTitle || record.title || record.name || getExperienceId(record));
  }

  function getPackageId(record) {
    return String(record.packageId || record.package || record.variantId || "");
  }

  function getPackageTitle(record) {
    return String(record.packageTitle || record.packageName || record.package || getPackageId(record));
  }

  function getRequiredQuantity(record) {
    const quantity = Number(record.requiredQuantity || record.quantity || 1);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function collectPackage(recordsByExperience, record) {
    const experienceId = getExperienceId(record);
    const packageId = getPackageId(record);

    if (!experienceId || !packageId) {
      return;
    }

    if (!recordsByExperience[experienceId]) {
      recordsByExperience[experienceId] = {
        id: experienceId,
        title: getExperienceTitle(record),
        packages: {}
      };
    }

    if (!recordsByExperience[experienceId].packages[packageId]) {
      recordsByExperience[experienceId].packages[packageId] = {
        id: packageId,
        title: getPackageTitle(record),
        requiredEquipment: [],
        requiredStaff: []
      };
    }
  }

  function buildExperienceIndex(equipment, staff) {
    const recordsByExperience = {};

    equipment.forEach(function (record) {
      collectPackage(recordsByExperience, record);

      const experienceId = getExperienceId(record);
      const packageId = getPackageId(record);

      if (experienceId && packageId && recordsByExperience[experienceId]) {
        recordsByExperience[experienceId].packages[packageId].requiredEquipment.push(record);
      }
    });

    staff.forEach(function (record) {
      collectPackage(recordsByExperience, record);

      const experienceId = getExperienceId(record);
      const packageId = getPackageId(record);

      if (experienceId && packageId && recordsByExperience[experienceId]) {
        recordsByExperience[experienceId].packages[packageId].requiredStaff.push(record);
      }
    });

    return recordsByExperience;
  }

  function bookingConflictsWithPackage(booking, slot, experienceId, packageId) {
    const bookingItems = toArray(booking.items);

    if (!matchesSlot(booking, slot)) {
      return false;
    }

    if (bookingItems.length === 0) {
      return matchesValue(booking.experienceId || booking.experience, experienceId) &&
        matchesValue(booking.packageId || booking.package || booking.variantId, packageId);
    }

    return bookingItems.some(function (item) {
      return matchesValue(item.experienceId || item.experience, experienceId) &&
        matchesValue(item.packageId || item.package || item.variantId, packageId);
    });
  }

  function packageHasBookingConflict(bookings, slot, experienceId, packageId) {
    return bookings.some(function (booking) {
      return !isInactive(booking) && bookingConflictsWithPackage(booking, slot, experienceId, packageId);
    });
  }

  function resourceIsAvailable(record, slot) {
    return !isInactive(record) && matchesSlot(record, slot);
  }

  function packageHasResources(records, slot) {
    if (records.length === 0) {
      return true;
    }

    return records.every(function (record) {
      const availableQuantity = Number(record.availableQuantity || record.availableUnits || record.count || record.quantity || 1);

      return resourceIsAvailable(record, slot) &&
        Number.isFinite(availableQuantity) &&
        availableQuantity >= getRequiredQuantity(record);
    });
  }

  function serviceAreaMatches(area, location) {
    if (typeof area === "string") {
      return normalizeText(area) === normalizeText(location);
    }

    return matchesValue(area.location || area.city || area.name, location);
  }

  function getTransportation(slot, travel, serviceAreas) {
    const matchingArea = serviceAreas.find(function (area) {
      return serviceAreaMatches(area, slot.location);
    });

    if (matchingArea && isInactive(matchingArea)) {
      return {
        status: STATUS_OUTSIDE_SERVICE_AREA
      };
    }

    if (matchingArea) {
      return {
        status: matchingArea.transportationStatus || matchingArea.travelStatus || STATUS_INCLUDED
      };
    }

    if (toArray(travel.includedLocations).some(function (location) {
      return normalizeText(location) === normalizeText(slot.location);
    })) {
      return {
        status: STATUS_INCLUDED
      };
    }

    if (toArray(travel.excludedLocations).some(function (location) {
      return normalizeText(location) === normalizeText(slot.location);
    })) {
      return {
        status: STATUS_OUTSIDE_SERVICE_AREA
      };
    }

    if (serviceAreas.length === 0) {
      return {
        status: travel.status || STATUS_INCLUDED
      };
    }

    return {
      status: STATUS_OUTSIDE_SERVICE_AREA
    };
  }

  function getAvailablePackages(experience, slot, data) {
    return Object.keys(experience.packages).map(function (packageId) {
      const packageRecord = experience.packages[packageId];
      const hasConflict = packageHasBookingConflict(data.bookings, slot, experience.id, packageId);
      const hasEquipment = packageHasResources(packageRecord.requiredEquipment, slot);
      const hasStaff = packageHasResources(packageRecord.requiredStaff, slot);

      if (hasConflict || !hasEquipment || !hasStaff) {
        return null;
      }

      return {
        id: packageRecord.id,
        title: packageRecord.title
      };
    }).filter(Boolean);
  }

  function getAvailableExperiences(slot, data) {
    const experiences = buildExperienceIndex(data.equipment, data.staff);

    return Object.keys(experiences).map(function (experienceId) {
      const experience = experiences[experienceId];
      const availablePackages = getAvailablePackages(experience, slot, data);

      if (availablePackages.length === 0) {
        return null;
      }

      return {
        id: experience.id,
        title: experience.title,
        availablePackages: availablePackages
      };
    }).filter(Boolean);
  }

  function getMessages(slot, transportation, availableExperiences) {
    const messages = [];

    if (!slot.date || !slot.time || !slot.location) {
      messages.push("Date, time, and location are required.");
    }

    if (transportation.status === STATUS_OUTSIDE_SERVICE_AREA) {
      messages.push("The selected location is outside the service area.");
    }

    if (availableExperiences.length === 0) {
      messages.push("No experiences are available for the selected slot.");
    }

    return messages;
  }

  window.Naxarly = window.Naxarly || {};

  window.Naxarly.Availability = {
    /**
     * Check availability for a slot using the supplied bookings, equipment, staff, travel, and service area data.
     * @param {Object} slot Selected date, time, and location.
     * @param {Object} availabilityData Availability source data passed into the engine.
     * @returns {Object} Availability status, messages, transportation details, and available experiences.
     */
    check: function (slot, availabilityData) {
      const normalizedSlot = normalizeSlot(slot);
      const data = normalizeAvailabilityData(availabilityData);
      const transportation = getTransportation(normalizedSlot, data.travel, data.serviceAreas);
      const availableExperiences = transportation.status === STATUS_OUTSIDE_SERVICE_AREA ? [] : getAvailableExperiences(normalizedSlot, data);
      const messages = getMessages(normalizedSlot, transportation, availableExperiences);
      const status = messages.length === 0 && availableExperiences.length > 0 ? STATUS_AVAILABLE : STATUS_UNAVAILABLE;

      return {
        status: status,
        messages: messages,
        transportation: transportation,
        availableExperiences: availableExperiences
      };
    }
  };
})();